import { useCallback, useMemo } from 'react';

import dlv from 'dlv';
import { isNil } from 'lodash-es';
import { useIntl } from 'react-intl';

import { useUIContext } from 'state/ui';

import {
  FormatPlaceholderInterpolationFn,
  FormatValueFn,
  FormattersMap,
  PlaceholderField,
  PlaceholderFormatter,
} from './types';
import { buildFormattersMap, valueToString } from './use-placeholder-formatters-helpers';

const defaultPlaceholderField: PlaceholderField = {
  fieldId: '',
  path: '',
  formatter: PlaceholderFormatter.TO_STRING,
};

export const usePlaceholderFormatters = () => {
  const formatters = useIntl();
  const { formatCurrencyForLocale } = useUIContext();

  const formattersMap = useMemo<FormattersMap>(
    () => buildFormattersMap(formatters, formatCurrencyForLocale),
    [formatters, formatCurrencyForLocale]
  );

  const formatValue = useCallback<FormatValueFn>(
    (value, formatType = PlaceholderFormatter.TO_STRING) => {
      const formatter = formattersMap[formatType] || valueToString;
      return formatter(value);
    },
    [formattersMap]
  );

  const formatPlaceholderInterpolation = useCallback<FormatPlaceholderInterpolationFn>(
    ({ interpolationSourceObject, placeholderField = defaultPlaceholderField }) => {
      const value = dlv(interpolationSourceObject, placeholderField.path);
      return isNil(value) ? '' : formatValue(value, placeholderField.formatter);
    },
    [formatValue]
  );

  return {
    formatValue,
    formatPlaceholderInterpolation,
  };
};
